import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    TextField,
    Stack,
    StackItem,
    ITextFieldStyles,
    Text,
    Modal,
    IModalStyles,
    IRenderFunction,
    ITextFieldProps,
    Image,
    TooltipHost,
    Dropdown,
    IDropdownOption,
    IDropdownStyles,
    IDropdownProps,
} from '@fluentui/react';
import ButtonPrimaryComponent from '../../components/button-primary/button-primary.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import LoaderComponent from '../../components/loader/loader.component';
import SuccessMessageComponent from '../../components/success-message/success-message.component';
import TermsAndConditionsComponent from '../../components/terms-and-conditions/terms-and-conditions.component';
import PrivacyPolicyComponent from '../../components/privacy-policy/privacy-policy.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemCliente from '../../models/ItemCliente';
import ItemPais from '../../models/ItemPais';

import exclamationIcon from '../../assets/img16/info_16.png';

const textFieldStyles : ITextFieldStyles = {
    root: {},
    fieldGroup: {
        borderColor: 'var(--color-primario)',
        borderRadius: 5,
        selectors: {
            "&:hover, &:focus, &:active": {
                borderColor: 'var(--color-primario)',
                borderRadius: 5,
            },
        },
    },
    prefix: {},
    suffix: {},
    field: {},
    icon: {},
    description: {},
    wrapper: {},
    errorMessage: {},
    subComponentStyles: {
        label: {},
    },
    revealButton: {},
    revealSpan: {},
    revealIcon: {},
};

const dropdownStyles : IDropdownStyles = {
    root: {},
    errorMessage: {},
    label: {},
    dropdown: {
        borderColor: 'var(--color-primario)',
        borderRadius: 5,
        selectors: {
            "&:hover, &:focus, &:active": {
                borderColor: 'var(--color-primario)',
                borderRadius: 5,
            },
            "&:hover .ms-Dropdown-title": {
                borderColor: 'var(--color-primario)',
            },
        },
    },
    title: {
        borderColor: 'var(--color-primario)',
        borderRadius: 5,
        selectors: {
            "&:hover, &:focus, &:active": {
                borderColor: 'var(--color-primario)',
                borderRadius: 5,
            },
        },
    },
    caretDownWrapper: {},
    caretDown: {},
    dropdownItemsWrapper: {},
    dropdownItems: {},
    dropdownItem: {},
    dropdownItemSelected: {},
    dropdownItemDisabled: {},
    dropdownItemSelectedAndDisabled: {},
    dropdownItemHidden: {},
    dropdownOptionText: {},
    dropdownDivider: {},
    dropdownDividerHidden: {},
    dropdownItemHeader: {},
    dropdownItemHeaderHidden: {},
    panel: {},
    callout: {},
    subComponentStyles: {
        panel: {},
        label: {},
        multiSelectItem: {},
    },
};

const modalStyles : IModalStyles = {
    root: {},
    main: {
        height: '80vh',
        width: '80vw',
        padding: 25,
    },
    scrollableContent: {
        height: '100%',
    },
    layer: {},
    keyboardMoveIconContainer: {},
    keyboardMoveIcon: {},
};

const onRenderTextFieldLabel : IRenderFunction<ITextFieldProps> = (props?: ITextFieldProps, defaultRender?: (props?: ITextFieldProps) => JSX.Element | null) : JSX.Element => {
    const content = (title: string, description: string) : JSX.Element => {
        return (
            <Stack>
                <StackItem>
                    <Text className='fw-bold'>
                        {title}
                    </Text>
                </StackItem>
                <StackItem>
                    <Text>
                        {description}
                    </Text>
                </StackItem>
            </Stack>
        );
    };

    return props && props.label ? (
        <Stack 
            horizontal={true}
            className="d-flex flex-row align-items-center py-2">
            <StackItem>
                <Text>
                    {props?.label}
                </Text>
            </StackItem>
            {
                props.data ? (
                    <StackItem>
                        <TooltipHost 
                            content={content(props.label, props.data)}
                            tooltipProps={{
                                calloutProps: {
                                    styles: {
                                        beak: { 
                                            background: '#F8F9FA',
                                        },
                                        beakCurtain: { 
                                            background: '#F8F9FA',
                                        },
                                        calloutMain: { 
                                            background: '#F8F9FA',
                                        },
                                    },
                                },
                              }}>
                            <Image 
                                className='ms-1'
                                height={16}
                                width={16}
                                src={exclamationIcon} 
                                alt="Información" />
                        </TooltipHost>
                    </StackItem>
                ) : (
                    null
                )
            }
        </Stack>
    ) : (
        <></>
    );
};

const onRenderDropdownLabel : IRenderFunction<IDropdownProps> = (props?: IDropdownProps, defaultRender?: (props?: IDropdownProps) => JSX.Element | null) : JSX.Element => {
    const content = (title: string, description: string) : JSX.Element => {
        return (
            <Stack>
                <StackItem>
                    <Text className='fw-bold'>
                        {title}
                    </Text>
                </StackItem>
                <StackItem>
                    <Text>
                        {description}
                    </Text>
                </StackItem>
            </Stack>
        );
    };

    return props && props.label ? (
        <Stack 
            horizontal={true}
            className="d-flex flex-row align-items-center py-2">
            <StackItem>
                <Text>
                    {props?.label}
                </Text>
            </StackItem>
            {
                props.about ? (
                    <StackItem>
                        <TooltipHost 
                            content={content(props.label, props.about)}
                            tooltipProps={{
                                calloutProps: {
                                    styles: {
                                        beak: { 
                                            background: '#F8F9FA',
                                        },
                                        beakCurtain: { 
                                            background: '#F8F9FA',
                                        },
                                        calloutMain: { 
                                            background: '#F8F9FA',
                                        },
                                    },
                                },
                              }}>
                            <Image 
                                className='ms-1'
                                height={16}
                                width={16}
                                src={exclamationIcon} 
                                alt="Información" />
                        </TooltipHost>
                    </StackItem>
                ) : (
                    null
                )
            }
        </Stack>
    ) : (
        <></>
    );
};

interface IRegisterProfessionalPageProps {};

const RegisterProfessionalPage = (props: IRegisterProfessionalPageProps) => {
    const auth = useAuthStatus();
    
    const [registerError, setRegisterError] = useState<string>('');
    const [customer, setCustomer] = useState<ItemCliente>(new ItemCliente());
    const [isUseTermsOpen, setIsUseTermsOpen] = useState<boolean>(false);
    const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState<boolean>(false); 
    const [loading, setLoading] = useState<boolean>(false);
    const [step, setStep] = useState<number>(1);
    const [success, setSuccess] = useState<string>('');
    const [countries, setCountries] = useState<ItemPais[]>([]);

    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        ApiService.PostAnonym<ItemPais[]>("GetPaises").then((response: ItemPais[]) => {
            setCountries(response);
            setLoading(false);
        }, (error: string) => {
            setRegisterError(error);
            setLoading(false);
        });
    }, []);

    const onChangeEmail = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer, 
            Email: newValue ? newValue : "",
        });
    };

    const onChangePassword = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer, 
            P1: newValue ? newValue : "",
            P2: newValue ? newValue : "",
        });
    };

    const onChangeName = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer, 
            Nombre: newValue ? newValue : "",
        });
    };

    const onChangeNamespace = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer, 
            Espacio: newValue ? newValue : "",
        });
    };

    const onChangePrefix = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<ItemPais>, index?: number) : void => {
        setCustomer({ 
            ...customer, 
            Prefijo: (option && option.data) ? option.data.Prefijo : "",
        });
    };

    const onChangePhone = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setCustomer({ 
            ...customer, 
            Telefono: newValue ? newValue.trim().replaceAll(' ', '') : "",
        });
    };

    const onKeyUp = (event: React.KeyboardEvent) : void => {
        if (event.key === "Enter") {
            onRegister();
        }
    };

    const onRegister = () : void => {
        setRegisterError('');
        if (customer.Email === "" || customer.P1 === "" || customer.P2 === "" || customer.Nombre === "" || customer.Espacio === "" || customer.Prefijo === "" || 
            customer.Telefono === "") {
            setRegisterError("Alguno de los campos introducidos no es válido.");
        }
        else if (customer.Telefono.length < 9) {
            setRegisterError("El teléfono de contacto debe tener una longitud miníma de 9 dígitos.");
        }
        else {
            setLoading(true);
            auth.register(customer).then((response: string) => {
                setSuccess(response);
                setStep(2);
                setLoading(false);
            }, (error: string) => {
                setRegisterError(error);
                setLoading(false);
            });
        }
    };

    return (
        <>
            {
                (step === 1) ? (
                    <>
                        <Stack>
                            <StackItem className='mb-3'>
                                <Text 
                                    className='fw-bold'
                                    variant='large'>
                                    Registro profesional:
                                </Text>
                            </StackItem>
                            <StackItem className='mb-3'>
                                <Text>
                                    Crea una cuenta y comienza a usar MyMov gratis
                                </Text>
                            </StackItem>
                            <StackItem className='mb-3'>
                                <TextField
                                    type="email"
                                    autoComplete="on"
                                    maxLength={100}
                                    onChange={onChangeEmail}
                                    defaultValue={customer.Email}
                                    label="Correo electrónico"
                                    styles={textFieldStyles}
                                    data="Indica tu dirección de email, como propietario del espacio. Esta dirección será tu identificador de usuario."
                                    onRenderLabel={onRenderTextFieldLabel}
                                    onKeyUp={onKeyUp} />
                            </StackItem>
                            <StackItem className='mb-3'>
                                <TextField
                                    type="password"
                                    autoComplete="on"
                                    canRevealPassword={true}
                                    onChange={onChangePassword}
                                    defaultValue={customer.P1}
                                    label="Contraseña"
                                    styles={textFieldStyles}
                                    data="Indica tu contraseña."
                                    onRenderLabel={onRenderTextFieldLabel}
                                    onKeyUp={onKeyUp} />
                            </StackItem>
                            <StackItem className='mb-3'>
                                <TextField
                                    type="text"
                                    autoComplete="on"
                                    maxLength={100}
                                    onChange={onChangeName}
                                    defaultValue={customer.Nombre}
                                    label="Nombre completo del administrador"
                                    styles={textFieldStyles}
                                    data="Indícanos tu nombre completo, para poder dirigirnos a ti como propietario del espacio."
                                    onRenderLabel={onRenderTextFieldLabel}
                                    onKeyUp={onKeyUp} />
                            </StackItem>
                            <StackItem className='mb-3'>
                                <TextField
                                    type="text"
                                    autoComplete="on"
                                    maxLength={100}
                                    onChange={onChangeNamespace}
                                    defaultValue={customer.Espacio}
                                    label="Nombre para tu espacio de trabajo"
                                    styles={textFieldStyles}
                                    data="Indica el nombre con el que se identificará al espacio de trabajo común para tu equipo y proyecto."
                                    onRenderLabel={onRenderTextFieldLabel}
                                    onKeyUp={onKeyUp} />
                            </StackItem>
                            <StackItem className='mb-3'>
                                <div className='row m-0'>
                                    <div className='col-6 ps-0 pe-2'>
                                        <Dropdown 
                                            options={countries.map((country: ItemPais) => {
                                                return {
                                                    key: country.Id,
                                                    text: `${country.Nombre} (${country.Prefijo})`,
                                                    data: country,
                                                };
                                            })} 
                                            label="Prefijo"
                                            about="Indícanos un número de teléfono de contacto."
                                            onRenderLabel={onRenderDropdownLabel}
                                            styles={dropdownStyles}
                                            onChange={onChangePrefix} 
                                            placeholder="Prefijo"  />
                                    </div>
                                    <div className='col-6 ps-2 pe-0'>
                                        <TextField
                                            type="number"
                                            autoComplete="on"
                                            maxLength={50}
                                            onChange={onChangePhone}
                                            defaultValue={customer.Telefono}
                                            label="Teléfono de contacto"
                                            styles={textFieldStyles}
                                            data="Indícanos un número de teléfono de contacto."
                                            onRenderLabel={onRenderTextFieldLabel}
                                            onKeyUp={onKeyUp} />
                                    </div>
                                </div>
                            </StackItem>
                            <StackItem className='mb-3'>
                                <Text>
                                    Al pulsar <b>REGISTRAR</b>, aceptas los <Link 
                                        to="#" 
                                        onClick={() => setIsUseTermsOpen(true)}
                                        style={{
                                            color: 'var(--color-primario)',
                                        }}>
                                        Términos de uso
                                    </Link> y las <Link 
                                        to="#" 
                                        onClick={() => setIsPrivacyPolicyOpen(true)}
                                        style={{
                                            color: 'var(--color-primario)',
                                        }}>
                                        Políticas de privacidad
                                    </Link> de MyMov
                                </Text>
                            </StackItem>
                            <StackItem className='mb-3'>
                                <Stack 
                                    horizontal={true}
                                    className="d-flex align-items-center justify-content-between">
                                    <StackItem>
                                        <ButtonPrimaryComponent 
                                            className='text-uppercase'
                                            text='Registrar'
                                            onClick={onRegister} />
                                    </StackItem>
                                    <StackItem className='text-center flex-fill align-items-center'>
                                        <Link 
                                            to="/"
                                            style={{
                                                color: 'var(--color-primario)',
                                                textDecoration: 'none',
                                            }}>
                                            ¿Ya tienes una cuenta? Iniciar sesión.
                                        </Link>
                                    </StackItem>
                                </Stack>
                            </StackItem>
                            <StackItem className='mb-3'>
                                <ErrorMessageComponent message={registerError} />
                            </StackItem>
                        </Stack>
                        <Modal
                            titleAriaId="modal-use-terms"
                            isOpen={isUseTermsOpen}
                            onDismiss={() => setIsUseTermsOpen(false)}
                            isBlocking={true}
                            styles={modalStyles}
                            firstFocusableSelector="top">
                            <Stack className='top'>
                                <StackItem className='p-3'>
                                    <TermsAndConditionsComponent />
                                </StackItem>
                                <StackItem className='text-center my-5'>
                                    <ButtonPrimaryComponent
                                        text='Aceptar'
                                        className='text-uppercase'
                                        onClick={() => setIsUseTermsOpen(false)} />
                                </StackItem>
                            </Stack>
                        </Modal>
                        <Modal
                            titleAriaId="modal-privacy-policy"
                            isOpen={isPrivacyPolicyOpen}
                            onDismiss={() => setIsPrivacyPolicyOpen(false)}
                            isBlocking={true}
                            styles={modalStyles}
                            firstFocusableSelector="top">
                            <Stack className='top'>
                                <StackItem className='p-3'>
                                    <PrivacyPolicyComponent />
                                </StackItem>
                                <StackItem className='text-center my-5'>
                                    <ButtonPrimaryComponent
                                        text='Aceptar'
                                        className='text-uppercase'
                                        onClick={() => setIsPrivacyPolicyOpen(false)} />
                                </StackItem>
                            </Stack>
                        </Modal>
                    </>
                ) : (step === 2) ? (
                    <Stack>
                        <StackItem className='mb-3'>
                            <img 
                                src="https://ct.capterra.com/capterra_tracker.gif?vid=2225118&vkey=0dce4f2c7d891256c48e1e95696b84c4" 
                                alt="" />
                            <Text 
                                className='fw-bold'
                                variant='large'>
                                Registro profesional:
                            </Text>
                        </StackItem>
                        <StackItem className='mb-3'>
                            <SuccessMessageComponent message={success} />
                        </StackItem>
                        <StackItem className='mb-3'>
                            <ButtonPrimaryComponent 
                                className='text-uppercase'
                                text='Iniciar sesión'
                                onClick={() => navigate("/")} />
                        </StackItem>
                    </Stack>
                ) : (
                    null
                )
            }
            <LoaderComponent loading={loading} />
        </>
    );
};

export default RegisterProfessionalPage;
